import React, { useCallback, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n/I18nContext';
import { compressImage, ImageTooLargeError, MAX_SOURCE_BYTES } from '../../utils/compressImage';

export type PendingFile = {
  name: string;
  base64: string;
  preview: string;
};

type ImageUploadProps = {
  label: string;
  currentUrl?: string;
  pendingFile: PendingFile | null;
  onSelect: (file: PendingFile | null) => void;
  onClear: () => void;
};

export function ImageUpload({ label, currentUrl, pendingFile, onSelect, onClear }: ImageUploadProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      if (!file.type.startsWith('image/')) {
        setError(t('common.imageUpload.notImage'));
        return;
      }
      if (file.size > MAX_SOURCE_BYTES) {
        setError(t('common.imageUpload.tooLarge'));
        return;
      }

      setBusy(true);
      try {
        // Shrunk before it ever reaches the network: the API is behind a 1MB body cap
        // that rejects oversized uploads without CORS headers, which surfaces in the
        // browser as an unexplained CORS error rather than a size complaint.
        const { dataUrl, extension } = await compressImage(file);
        onSelect({
          name: `${Date.now()}.${extension}`,
          base64: dataUrl,
          preview: dataUrl,
        });
      } catch (err) {
        setError(
          err instanceof ImageTooLargeError
            ? t('common.imageUpload.tooLarge')
            : t('common.imageUpload.processFailed'),
        );
      } finally {
        setBusy(false);
      }
    },
    [onSelect, t]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      e.target.value = '';
    },
    [handleFile]
  );

  const displayUrl = pendingFile?.preview ?? currentUrl;
  const hasImage = !!displayUrl;

  return (
    <div>
      <div className="mb-1.5 text-sm font-medium text-slate-800 dark:text-slate-200">{label}</div>

      {hasImage ? (
        <div>
          <div className="relative group inline-block max-w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
            <img
              src={displayUrl}
              alt={label}
              className="h-32 w-auto max-w-full object-contain sm:max-w-xs"
            />
            {/* Hover overlay only works with a pointer, so phones get the button row below. */}
            <div className="absolute inset-0 hidden items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 md:flex">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-slate-800 shadow hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
              >
                {t('common.imageUpload.replace')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  onSelect(null);
                  onClear();
                }}
                className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-rose-600 shadow hover:bg-rose-50 dark:bg-slate-800 dark:hover:bg-rose-950"
              >
                {t('common.imageUpload.remove')}
              </button>
            </div>
          </div>

          <div className="mt-2 flex gap-2 md:hidden">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {t('common.imageUpload.replace')}
            </button>
            <button
              type="button"
              onClick={() => {
                setError(null);
                onSelect(null);
                onClear();
              }}
              className="flex-1 rounded-md border border-rose-200 bg-white px-3 py-2 text-xs font-medium text-rose-600 dark:border-rose-900 dark:bg-slate-900 dark:text-rose-400"
            >
              {t('common.imageUpload.remove')}
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => { if (!busy) inputRef.current?.click(); }}
          className={cn(
            'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-6 text-center transition sm:px-6 sm:py-8',
            busy ? 'cursor-wait opacity-60' : 'cursor-pointer',
            dragOver
              ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
              : 'border-slate-300 bg-slate-50 hover:border-red-300 hover:bg-red-50/50 dark:border-slate-600 dark:bg-slate-900 dark:hover:border-red-500 dark:hover:bg-red-950/20'
          )}
        >
          <svg className="mb-2 h-8 w-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.338-2.32 3 3 0 013.548 3.598A3.75 3.75 0 0118 19.5H6.75z" />
          </svg>
          {busy ? (
            <p className="text-sm text-slate-600 dark:text-slate-300">{t('common.imageUpload.processing')}</p>
          ) : (
            <>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {t('common.imageUpload.dragDrop')}{' '}
                <span className="font-medium text-red-600 dark:text-red-400">{t('common.imageUpload.browse')}</span>
              </p>
              <p className="mt-1 text-xs text-slate-400">{t('common.imageUpload.sizeHint')}</p>
            </>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        className="hidden"
        onChange={handleChange}
      />
      {error ? (
        <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">{error}</p>
      ) : busy && hasImage ? (
        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">{t('common.imageUpload.processing')}</p>
      ) : pendingFile ? (
        <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">{t('common.imageUpload.newImageSelected')}</p>
      ) : null}
    </div>
  );
}
